/**
 * LEFTY'S — product page helpers.
 *
 * <lefty-pdp-title>   On phones, moves the title/price block above the media gallery
 *                     (SAXX-style) and back into the buy box on wider screens.
 * <lefty-swatch-row>  Scroll arrows + hover label for the Solids / Patterns rows.
 */

const MOBILE_QUERY = '(max-width: 749px)';

class LeftyPdpTitle extends HTMLElement {
  /** @type {MediaQueryList | undefined} */
  #mql;
  /** @type {Comment | null} */
  #anchor = null;
  #onChange = () => this.#place();

  connectedCallback() {
    if (this.dataset.mobileTop !== 'true') return;
    this.#mql = window.matchMedia(MOBILE_QUERY);
    this.#mql.addEventListener('change', this.#onChange);
    this.#place();
  }

  disconnectedCallback() {
    this.#mql?.removeEventListener('change', this.#onChange);
  }

  #place() {
    if (!this.#mql) return;
    const section = this.closest('.product-information');
    const slot = section?.querySelector('.lefty-pdp-mobile-slot');
    if (!slot) return;

    if (this.#mql.matches) {
      if (this.parentElement === slot) return;
      if (!this.#anchor) {
        this.#anchor = document.createComment('lefty-pdp-title');
        this.before(this.#anchor);
      }
      slot.append(this);
      slot.removeAttribute('hidden');
    } else if (this.parentElement === slot) {
      if (this.#anchor && this.#anchor.parentNode) {
        this.#anchor.replaceWith(this);
      } else {
        // Fallback: put it back at the top of the buy box.
        section?.querySelector('.product-details .group-block-content, .product-details')?.prepend(this);
      }
      this.#anchor = null;
      if (!slot.children.length) slot.setAttribute('hidden', '');
    }
  }
}

class LeftySwatchRow extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.label = this.querySelector('[data-selected-label]');
    this.arrows = this.querySelectorAll('[data-dir]');
    if (!this.track) return;

    const current = this.track.querySelector('[data-swatch-current="true"]');
    if (this.label && current && !this.label.textContent.trim()) {
      this.label.textContent = current.dataset.swatchLabel || '';
    }
    this.defaultLabel = this.label ? this.label.textContent : '';

    this.arrows.forEach((btn) => btn.addEventListener('click', this.#scroll));
    this.track.addEventListener('mouseover', this.#hover);
    this.track.addEventListener('mouseout', this.#unhover);
    this.track.addEventListener('focusin', this.#hover);
    this.track.addEventListener('focusout', this.#unhover);
    this.track.addEventListener('scroll', this.#updateArrows, { passive: true });
    window.addEventListener('resize', this.#updateArrows, { passive: true });
    this.#updateArrows();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#updateArrows);
  }

  #scroll = (event) => {
    const dir = Number(event.currentTarget.dataset.dir) || 1;
    const amount = Math.max(this.track.clientWidth * 0.7, 80);
    this.track.scrollBy({ left: amount * dir, behavior: 'smooth' });
  };

  #hover = (event) => {
    const swatch = event.target.closest('[data-swatch-label]');
    if (!swatch || !this.label) return;
    this.label.textContent = swatch.dataset.swatchLabel || this.defaultLabel;
  };

  #unhover = () => {
    if (this.label) this.label.textContent = this.defaultLabel;
  };

  #updateArrows = () => {
    if (!this.track || !this.arrows.length) return;
    const overflow = this.track.scrollWidth - this.track.clientWidth > 2;
    this.toggleAttribute('data-overflow', overflow);
    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1;
    this.arrows.forEach((btn) => {
      const dir = Number(btn.dataset.dir) || 1;
      btn.disabled = dir < 0 ? atStart : atEnd;
    });
  };
}

if (!customElements.get('lefty-pdp-title')) customElements.define('lefty-pdp-title', LeftyPdpTitle);
if (!customElements.get('lefty-swatch-row')) customElements.define('lefty-swatch-row', LeftySwatchRow);
